import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy, Inject } from "@angular/core";
import { FormArray, FormGroup, FormControl, Validators } from '@angular/forms';
import { Subject, Observable, combineLatest } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store, Select } from '@ngxs/store';
import { Dialog, Snackbar } from '@core/material';
import { InitImagesForm, ImagesFormState, ImagesFormStateModel, UploadImage, PendingUploadModel, SubmitImagesForm, InitTastingEventImagesForm, UploadTastingsEventImage, SubmitTastingEventImagesForm, InitTastingSampleImagesForm, SubmitTastingSampleImagesForm, UploadTastingSampleImage } from './images-form.state';
import { Evaluation, Status, SampleImage, SampleType, ImageTransferResult, ImagesUpdateRequest, TastingsEventImagesUpdateRequest, TastingSampleImagesUpdateRequest } from '@core/data';
import { takeUntil, debounceTime, map } from 'rxjs/operators';
import * as moment from 'moment';
import { coerceMomentProperty, fileExtension, coerceTimestampProperty } from '@core/utils';
import { TastingEvent, TastingsEventImage } from "@core/data/types/tastings-event";
import { TastingSample, TastingSampleImage } from "@core/data/types/tasting-sample";
import { ImageMoveState } from '../../image-move/image-move.state';
import { ImageMoveImageType } from '../../image-move/image-move.state-model';
import { ClearImageSelection, ToggleImageSelection, UndoImageTransfer } from '../../image-move/image-move.state-actions';
import { ImageMoveDestinationPickerDialog, ImageMoveDestinationPickerDialogData, ImageMoveDestinationPickerResult } from '../../image-move/image-move-destination-picker.dialog';


export interface ImagesFormDialogData {
    key: string;
    type?: string;
    // Sample-Evaluation-images-only: populated by the caller so this dialog can host
    // Sample Images' select+move UI (2026-09-15). Unused/undefined for Tastings types.
    sampleKey?: string;
    orgKey?: string;
    sampleType?: SampleType;
    allEvaluations?: Evaluation[];
}

export enum ImageUploadType {
    TASTINGS_EVENT = 'tastingsEvent',
    TASTINGS_SAMPLE = 'tastingsSample',
    SAMPLE_EVALUATION = 'sampleEvaluation',
}

@Component({
    selector: 'pv-images-form-dialog',
    templateUrl: 'images-form.dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-images-form-dialog pv-fullscreen-dialog'
    }
})
export class ImagesFormDialog implements OnInit, OnDestroy{

    @Select(ImagesFormState)
    state$: Observable<ImagesFormStateModel>;

    @Select(ImagesFormState.data)
    data$: Observable<Evaluation>;

    @Select(ImagesFormState.uploads)
    uploads$: Observable<PendingUploadModel[]>;

    formArray = new FormArray([]);
    formGroup = new FormGroup({
        // noop control to prevent formgroup being
        // disabled if all image controls is disabled
        _noop: new FormControl(true),
        images: this.formArray,
    });

    isDragging = false;
    maxSize = 40000000; // 40MB
    allowedExts = ['jpg', 'jpeg', 'png'];
    validationErrors: string[] = [];
    canUpload = true;

    imageUploadTypes = {
        sampleEvaluation: ImageUploadType.SAMPLE_EVALUATION,
        tastingsEvent: ImageUploadType.TASTINGS_EVENT,
        tastingsSample: ImageUploadType.TASTINGS_SAMPLE,
    }

    // 2026-09-15: Sample Images' select+move UI, merged in from the inline pv-image-gallery
    // grid (ImageGalleryComponent) so this dialog matches CharacteristicImageGalleryDialog's
    // all-in-one pattern. Sample-Evaluation-images-only — see the *ngIf gating in the template.
    @Select(ImageMoveState.selectedKeys)
    selectedKeys$: Observable<string[]>;

    @Select(ImageMoveState.sourceEvalKey)
    sourceEvalKey$: Observable<string>;

    @Select(ImageMoveState.imageType)
    imageType$: Observable<ImageMoveImageType>;

    @Select(ImageMoveState.transferResults)
    transferResults$: Observable<{ [imageKey: string]: ImageTransferResult }>;

    selectionCount$: Observable<number>;
    showToolbar$: Observable<boolean>;

    private _selectedKeys: string[] = [];
    // image_move is shared across Sample and Characteristic Images (keyed only by
    // sourceEvalKey) - only ever treat a selection as "its own" when imageType also
    // matches, matching the guard ImageGalleryComponent already established.
    private _imageType: ImageMoveImageType = null;
    private _transferResults: { [imageKey: string]: ImageTransferResult } = {};
    private _pendingUndo: { [imageKey: string]: Partial<SampleImage> } = {};

    private pushedUploadIds: number[] = [];
    private _destroy$ = new Subject();

    constructor(
        private _dialogRef: MatDialogRef<ImagesFormDialog>,
        private _store: Store,
        private _dialog: Dialog,
        private _snackbar: Snackbar,
        private _changeDetectorRef: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: ImagesFormDialogData
    ){
        this.selectionCount$ = combineLatest([this.selectedKeys$, this.imageType$]).pipe(
            map(([keys, imageType]) => imageType === 'sample' ? keys.length : 0)
        );

        this.showToolbar$ = combineLatest([this.selectedKeys$, this.sourceEvalKey$, this.imageType$]).pipe(
            map(([keys, sourceEvalKey, imageType]) =>
                keys.length > 0 && sourceEvalKey === this.data.key && imageType === 'sample')
        );
    }


    ngOnInit(){
        //Set default type
        if(this.data.type === undefined) this.data.type = 'sampleEvaluation';
        //Handle type of upload
        if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) this.handleSampleEvaluationImage()
        if (this.data.type === ImageUploadType.TASTINGS_EVENT) this.handleTastingEventImage()
        if (this.data.type === ImageUploadType.TASTINGS_SAMPLE) this.handleTastingSampleImage()

        if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) {
            this.selectedKeys$.pipe(takeUntil(this._destroy$)).subscribe(keys => {
                this._selectedKeys = keys;
                this._changeDetectorRef.markForCheck();
            });

            this.imageType$.pipe(takeUntil(this._destroy$)).subscribe(imageType => {
                this._imageType = imageType;
                this._changeDetectorRef.markForCheck();
            });

            this.transferResults$.pipe(takeUntil(this._destroy$)).subscribe(results => {
                this._transferResults = results;
                this._changeDetectorRef.markForCheck();
            });
        }
    }

    private handleSampleEvaluationImage() {
        this._store.dispatch(new InitImagesForm(this.data.key));

        this.state$
            .pipe(takeUntil(this._destroy$))
            .subscribe(state => {

                if(state.status === Status.COMPLETE){
                    this._snackbar.info("Images updated");
                    this.cancel();
                }else if(state.status !== Status.LOADING){
                    this.formGroup.enable({onlySelf: true});
                }else{
                    this.formGroup.disable({onlySelf: true});
                }

            });

        this.data$.pipe(takeUntil(this._destroy$))
            .subscribe(data => {
                if (data) this.buildForm(data);
            });


        // check for completed uploads to add to the form
        this.uploads$.pipe(
                takeUntil(this._destroy$),
                debounceTime(500)
            )
            .subscribe(uploads => {

                if(Array.isArray(uploads) && uploads.length < 1) return;

                uploads.forEach(upload => {
                    if(
                        upload.status === Status.COMPLETE
                        && upload.data
                        && !this.pushedUploadIds.includes(upload.id)
                        ){

                        this.pushedUploadIds.push(upload.id);
                        this.pushUploadedImage(upload.data as SampleImage);

                    }
                });

            });
    }

    private handleTastingEventImage() {
        this._store.dispatch(new InitTastingEventImagesForm(this.data.key));

        this.state$
            .pipe(takeUntil(this._destroy$))
            .subscribe(state => {

                if(state.status === Status.COMPLETE){
                    this._snackbar.info("Images updated");
                    this.cancel();
                }else if(state.status !== Status.LOADING){
                    this.formGroup.enable({onlySelf: true});
                }else{
                    this.formGroup.disable({onlySelf: true});
                }

                if (state.data) this.canUpload = (state.data as TastingEvent).tastingsEventImage ? false : true;
            });

        this.data$.pipe(takeUntil(this._destroy$))
            .subscribe(data => {
                if (data) this.buildForm(data);
            });

        // check for completed uploads to add to the form
        this.uploads$.pipe(
            takeUntil(this._destroy$),
            debounceTime(500)
        )
        .subscribe(uploads => {

            if(Array.isArray(uploads) && uploads.length < 1) return;
            if(Array.isArray(uploads) && uploads.length == 1 && this.data.type == ImageUploadType.TASTINGS_EVENT) this.canUpload = false;

            uploads.forEach(upload => {
                if(
                    upload.status === Status.COMPLETE
                    && upload.data
                    && !this.pushedUploadIds.includes(upload.id)
                    ){

                    this.pushedUploadIds.push(upload.id);
                    this.pushUploadedImage(upload.data as TastingsEventImage);

                }
            });

        });
    }

    private handleTastingSampleImage() {
        this._store.dispatch(new InitTastingSampleImagesForm(this.data.key));

        this.state$
            .pipe(takeUntil(this._destroy$))
            .subscribe(state => {

                if(state.status === Status.COMPLETE){
                    this._snackbar.info("Images updated");
                    this.cancel();
                }else if(state.status !== Status.LOADING){
                    this.formGroup.enable({onlySelf: true});
                }else{
                    this.formGroup.disable({onlySelf: true});
                }

                if (state.data) this.canUpload = (state.data as TastingSample).tastingsSampleImage ? false : true;
            });

        this.data$.pipe(takeUntil(this._destroy$))
            .subscribe(data => {
                if (data) this.buildForm(data);
            });

        // check for completed uploads to add to the form
        this.uploads$.pipe(
            takeUntil(this._destroy$),
            debounceTime(500)
        )
        .subscribe(uploads => {

            if(Array.isArray(uploads) && uploads.length < 1) return;
            if(Array.isArray(uploads) && uploads.length == 1 && this.data.type == ImageUploadType.TASTINGS_SAMPLE) this.canUpload = false;

            uploads.forEach(upload => {
                if(
                    upload.status === Status.COMPLETE
                    && upload.data
                    && !this.pushedUploadIds.includes(upload.id)
                    ){

                    this.pushedUploadIds.push(upload.id);
                    this.pushUploadedImage(upload.data as TastingSampleImage);

                }
            });

        });
    }

    ngOnDestroy(){
        // Prevent a stale selection/toolbar reappearing next time this Evaluation's
        // Edit Images dialog reopens in the same session - a gap already found and
        // logged twice before (move-2-2, move-3-2 reviews) in the other two galleries
        // sharing this same image_move state; built in from day one here.
        if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) {
            this._store.dispatch(new ClearImageSelection());
        }
        this._destroy$.next();
        this._destroy$.complete();
    }

    isSelected(imageKey: string): boolean {
        return this._imageType === 'sample' && this._selectedKeys.includes(imageKey);
    }

    failureReason(imageKey: string): string | null {
        const result = this._transferResults[imageKey];
        if (!result || result.status !== 'failed') return null;
        return result.reason || 'Move failed';
    }

    toggleSelect(image: Partial<SampleImage>) {
        // This dialog's cards are backed by FormGroup values (key/fileRef/note/takenAt) with
        // no evalKey control - ToggleImageSelection's handler requires image.evalKey (used to
        // detect a fresh batch vs. an in-progress one), so it must be added here. Every image
        // in this dialog belongs to the same Evaluation (this.data.key).
        this._store.dispatch(new ToggleImageSelection({ ...image, evalKey: this.data.key } as SampleImage, 'sample'));
    }

    clearSelection() {
        this._store.dispatch(new ClearImageSelection());
    }

    openMoveDialog() {

        const data: ImageMoveDestinationPickerDialogData = {
            sampleKey: this.data.sampleKey,
            sourceEvalKey: this.data.key,
            imageKeys: this._selectedKeys,
            evaluations: (this.data.allEvaluations || []).filter(evalu => evalu.key !== this.data.key),
            orgKey: this.data.orgKey,
            sampleType: this.data.sampleType,
            imageType: 'sample',
        };

        this._dialog.open(ImageMoveDestinationPickerDialog, { data, width: '520px' })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe((result: ImageMoveDestinationPickerResult) => {
                if (!result) return;
                this.handleTransferResult(result);
            });
    }

    private handleTransferResult(result: ImageMoveDestinationPickerResult) {

        const succeededKeys = result.results
            .filter(r => r.status === 'success')
            .map(r => r.imageKey);

        if (succeededKeys.length === 0) return;

        // The moved images no longer belong to this Evaluation - remove their
        // FormGroups from the array, keeping the raw value so Undo can rebuild
        // the group without a round-trip fetch.
        for (let i = this.formArray.length - 1; i >= 0; i--) {
            const value: Partial<SampleImage> = this.formArray.at(i).value;
            if (succeededKeys.includes(value.key)) {
                this._pendingUndo[value.key] = value;
                this.formArray.removeAt(i);
            }
        }
        this._changeDetectorRef.markForCheck();

        this._snackbar.action(`Moved to '${result.destinationLabel}'`, 'Undo')
            .onAction()
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this.undoTransfer(succeededKeys, result.destinationEvalKey));
    }

    private undoTransfer(imageKeys: string[], previousEvalKey: string) {

        this._store.dispatch(new UndoImageTransfer(imageKeys, this.data.key, 'sample', previousEvalKey))
            .subscribe({
                next: () => {

                    // Undo is itself just a forward transfer - only restore items the
                    // server actually reports as moved back; a partial undo failure
                    // must not silently look like a full success.
                    const results = this._store.selectSnapshot(ImageMoveState.transferResults);
                    const succeededKeys = imageKeys.filter(key => results[key]?.status === 'success');
                    const failedKeys = imageKeys.filter(key => !succeededKeys.includes(key));

                    succeededKeys.forEach(key => {
                        const restored = this._pendingUndo[key];
                        if (restored) this.formArray.push(this.makeImageGroup(restored));
                        delete this._pendingUndo[key];
                    });

                    if (failedKeys.length > 0) {
                        this._snackbar.error('Undo failed for one or more images. Please try again.');
                    }

                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    this._snackbar.error('Undo failed. Please try again.');
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    attempt(){

        if(this.formGroup.valid){

            let request = null;
            if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) request = this.getUpdateRequest();
            if (this.data.type === ImageUploadType.TASTINGS_EVENT) request = this.getTastingsEventUpdateRequest();
            if (this.data.type === ImageUploadType.TASTINGS_SAMPLE) request = this.getTastingSampleUpdateRequest();

            if(request.images.length){

                if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) this._store.dispatch(new SubmitImagesForm(request as ImagesUpdateRequest));
                if (this.data.type === ImageUploadType.TASTINGS_EVENT) this._store.dispatch(new SubmitTastingEventImagesForm(request as TastingsEventImagesUpdateRequest));
                if (this.data.type === ImageUploadType.TASTINGS_SAMPLE) this._store.dispatch(new SubmitTastingSampleImagesForm(request as TastingSampleImagesUpdateRequest));

            }else{
                this._snackbar.info("No changes to images.");
                this.cancel();
            }


        }else{
            this._snackbar.error("One or more notes has an invalid date/observation. Check your input and try again.");
        }

    }

    cancel() {
        this._dialogRef.close();
    }

    asEval(obj) : Evaluation {return obj};
    asEvent(obj) : TastingEvent {return obj};
    asTasteSample(obj) : TastingSample {return obj};

    toggleDelete(index: number) {
        let group = <FormGroup>this.formArray.at(index);

        if (!group) return;

        if (group.disabled) {
            group.enable();
        }else{
            group.disable();

            // Marking an already-selected Sample Image for deletion must also drop it from
            // the active move selection - otherwise it can still be moved via the toolbar
            // (formArray.removeAt in handleTransferResult), silently discarding the pending
            // delete with no warning (code review finding).
            const key = group.value?.key;
            if (this.data.type === ImageUploadType.SAMPLE_EVALUATION && key && this.isSelected(key)) {
                this.toggleSelect(group.value);
            }
        }

    }

    fileInputChanged(event: Event){

        let input = <HTMLInputElement> event.target;

        if(input.files && input.files.length > 0){
            this.uploadFiles(input.files);
        }

    }

    onDragOver(event: DragEvent){

        if(event.dataTransfer){
            event.dataTransfer.dropEffect = "copy";
        }

        if(!this.isDragging){
            this.isDragging = true;
        }
        event.preventDefault();
        event.stopPropagation();
    }

    onDragLeave(event: DragEvent){

        if(this.isDragging){
            this.isDragging = false;
        }

        event.preventDefault();
    }

    onDrop(event: DragEvent){

        if(event.dataTransfer){
            event.dataTransfer.dropEffect = 'copy';

            this.uploadFiles(event.dataTransfer.files);
        }

        this.isDragging = false;

        event.preventDefault();
        event.stopPropagation();
    }

    trackUpload(index, item: PendingUploadModel){
        return item.id;
    }

    private pushUploadedImage(image: SampleImage | TastingsEventImage | TastingSampleImage) {
        if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) this.formArray.push(this.makeImageGroup(image as SampleImage));
        else if (this.data.type === ImageUploadType.TASTINGS_EVENT) this.formArray.push(this.makeImageGroup(image as TastingsEventImage));
        else if (this.data.type === ImageUploadType.TASTINGS_SAMPLE) this.formArray.push(this.makeImageGroup(image as TastingSampleImage));
    }

    private uploadFiles(list: FileList){
        if(this.validateFiles(list)){
            for(let i = 0; i < list.length; i++){
                if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) this._store.dispatch(new UploadImage(list.item(i)));
                if (this.data.type === ImageUploadType.TASTINGS_EVENT) this._store.dispatch(new UploadTastingsEventImage(list.item(i)));
                if (this.data.type === ImageUploadType.TASTINGS_SAMPLE) this._store.dispatch(new UploadTastingSampleImage(list.item(i)));
            }
        }

    }

    private buildForm(model: Evaluation | TastingEvent | TastingSample) {

        this.formArray.clear();
        this.formGroup.reset({ images: [] });

        if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) {
            this.asEval(model).images.forEach(note => {
                let control = this.makeImageGroup(note);
                this.formArray.push(control);
            });
        }

        if (this.data.type === ImageUploadType.TASTINGS_EVENT) {
            if (this.asEvent(model).tastingsEventImage) {
                let control = this.makeImageGroup(this.asEvent(model).tastingsEventImage);
                this.formArray.push(control);
            }
        }

        if (this.data.type === ImageUploadType.TASTINGS_SAMPLE) {
            if (this.asTasteSample(model).tastingsSampleImage) {
                let control = this.makeImageGroup(this.asTasteSample(model).tastingsSampleImage);
                this.formArray.push(control);
            }
        }
    }

    private makeImageGroup(image: Partial<SampleImage | TastingsEventImage | TastingSampleImage>) {
        if (this.data.type === ImageUploadType.SAMPLE_EVALUATION) {
            return new FormGroup({
                key: new FormControl((image as SampleImage).key || null),
                fileRef: new FormControl((image as SampleImage).fileRef || null),
                note: new FormControl((image as SampleImage).note || null, [Validators.maxLength(250)]),
                takenAt: new FormControl(coerceMomentProperty((image as SampleImage).takenAt), []),
            });
        }

        if (this.data.type === ImageUploadType.TASTINGS_EVENT) {
            return new FormGroup({
                key: new FormControl((image as TastingsEventImage).key || null),
                fileRef: new FormControl((image as TastingsEventImage).fileRef || null),
            });
        }

        if (this.data.type === ImageUploadType.TASTINGS_SAMPLE) {
            return new FormGroup({
                key: new FormControl((image as TastingSampleImage).key || null),
                fileRef: new FormControl((image as TastingSampleImage).fileRef || null),
            });
        }
    }

    private validateFiles(files: FileList){

        let valid = true;
        this.validationErrors = [];

        for(let i = 0; i < files.length; i++){
            let file = files[i];
            let fileExt = fileExtension(file.name).toLowerCase();

            if(!this.allowedExts.includes(fileExt)){
                this.validationErrors.push(`${file.name} is not a valid file type.`);
            }

            if(file.size > this.maxSize){
                this.validationErrors.push(`${file.name} exceeds the maximum file size.`);
            }

        }

        if(this.validationErrors.length > 0){
            valid = false;
        }

        return valid;
    }

    private getUpdateRequest(): ImagesUpdateRequest {

        let imageUpdates: Array<Partial<SampleImage>> = [];

        let value: Partial<SampleImage>;

        this.formArray.controls.forEach(control => {

            value = control.value;

            if(control.disabled){
                // disabled indicates delete

                imageUpdates.push({
                    key: value.key,
                    deletedAt: coerceTimestampProperty(moment())
                });

            }else if(control.dirty){

                imageUpdates.push({
                    key: value.key,
                    note: value.note,
                    takenAt: coerceTimestampProperty(value.takenAt)
                });

            }

        });


        return {
            images: imageUpdates
        };
    }

    private getTastingsEventUpdateRequest(): TastingsEventImagesUpdateRequest {
        const state: ImagesFormStateModel = this._store.selectSnapshot(ImagesFormState);

        var imageUpdates: Array<Partial<TastingsEventImage>> = [];

        var value: Partial<TastingsEventImage>;

        this.formArray.controls.forEach(control => {

            value = control.value;

            if (control.disabled) {
                // disabled indicates delete
                imageUpdates.push({
                    key: value.key,
                    deletedAt: coerceTimestampProperty(moment()),
                });

            } else {
                imageUpdates.push({
                    key: value.key,
                });
            }

        });

        return {
            images: imageUpdates,
            ownerOrgKey: (state.data as TastingEvent).ownerOrgKey,
        };
    }

    private getTastingSampleUpdateRequest(): TastingSampleImagesUpdateRequest {
        const state: ImagesFormStateModel = this._store.selectSnapshot(ImagesFormState);

        var imageUpdates: Array<Partial<TastingSampleImage>> = [];

        var value: Partial<TastingSampleImage>;

        this.formArray.controls.forEach(control => {

            value = control.value;

            if (control.disabled) {
                // disabled indicates delete
                imageUpdates.push({
                    key: value.key,
                    deletedAt: coerceTimestampProperty(moment()),
                });

            } else {
                imageUpdates.push({
                    key: value.key,
                });
            }

        });

        return {
            images: imageUpdates,
            ownerOrgKey: (state.data as TastingSample).ownerOrgKey,
        };
    }
}